import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * Trigger a native browser download of the analysis PDF.
 *
 * Strategy:
 *   We build a direct URL to the backend PDF endpoint that includes the JWT in a
 *   query parameter (the endpoint supports BOTH Authorization header and ?token=).
 *   Then we create an <a> element with `download` attribute and click it.
 *   The browser performs a real HTTP GET and handles the response natively —
 *   because the server returns `Content-Disposition: attachment`, the file is
 *   saved to the user's Downloads folder directly. No blob/fetch dance needed,
 *   which is the most reliable cross-browser approach.
 */
export function downloadAnalysisPdf(analysisId) {
  const token = localStorage.getItem("trico_token");
  if (!token) {
    toast.error("Sessione scaduta, effettua di nuovo il login.");
    return false;
  }

  const url = `${API}/analyses/${encodeURIComponent(analysisId)}/pdf?token=${encodeURIComponent(token)}`;

  try {
    const a = document.createElement("a");
    a.href = url;
    // `download` hint — server also sets Content-Disposition which is authoritative
    a.download = "";
    // Keep the current tab; the browser handles the attachment as a download
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    // Remove node after a short delay (the request is in-flight)
    setTimeout(() => {
      try { a.remove(); } catch { /* ignore */ }
    }, 1000);
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[PDF] native download failed, falling back to window.open", e);
    const w = window.open(url, "_self");
    if (!w) {
      toast.error("Il browser ha bloccato il download. Verifica le impostazioni.");
      return false;
    }
    return true;
  }
}

/**
 * Fetch the analysis PDF as a File object, ready to be passed to
 * `navigator.share({ files: [file] })` on mobile.
 * Returns `null` on any failure so the caller can fall back gracefully.
 */
export async function fetchAnalysisPdfFile(analysisId, filename = "report-tricologico.pdf") {
  const token = localStorage.getItem("trico_token");
  if (!token) return null;
  const url = `${API}/analyses/${encodeURIComponent(analysisId)}/pdf?token=${encodeURIComponent(token)}`;
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return null;
    const blob = await res.blob();
    return new File([blob], filename, { type: "application/pdf" });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[PDF] fetch as File failed", e);
    return null;
  }
}

/**
 * Feature detection for file-capable Web Share API.
 * Many desktop browsers expose `navigator.share` but NOT the ability to share
 * files — we must explicitly probe `canShare({ files })` with a real File stub.
 */
export function canShareFiles() {
  if (typeof navigator === "undefined") return false;
  if (!navigator.share || !navigator.canShare) return false;
  try {
    const probe = new File([""], "probe.pdf", { type: "application/pdf" });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
}
